import React, { useState, useEffect, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import * as Bixby from '$/core';
import { AppState } from '$/store';
import { toastSuccess, toastError } from '$/store/toast/actions';
import { AgGridReact } from 'ag-grid-react';
import Loading from '$/components/loading/Loading';
import BixButton from '$/components/global/bixButton';
import Modal from '$/components/global/modal';
import { ClientAdminService } from '$/services';
import './ClientOnboarding.scss';

interface IClientOnboardingProps {
  client?: Bixby.Models.Client;
  isLoading: boolean;
  dispatchToastSuccess: (message: string) => void;
  dispatchToastError: (message: string) => void;
}

const ClientOnboarding: React.FC<IClientOnboardingProps> = props => {
  const { client, isLoading, dispatchToastSuccess, dispatchToastError } = props;
  const [documents, setDocuments] = useState<Bixby.Models.OnboardingDocumentSummary[]>([]);
  const [isFetchingDocuments, setIsFetchingDocuments] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const gridApiRef = useRef<any>(null);

  const fetchDocuments = useCallback(async () => {
    if (!client) return;
    setIsFetchingDocuments(true);
    try {
      const result = await ClientAdminService.getOnboardingDocuments(client.id);
      setDocuments(result ?? []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch onboarding documents', err);
    } finally {
      setIsFetchingDocuments(false);
    }
  }, [client]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleBeginImport = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmImport = async () => {
    if (!client) return;
    setShowConfirmModal(false);
    setIsImporting(true);
    try {
      await ClientAdminService.enqueueOnboardingImport(client.id);
      dispatchToastSuccess('Onboarding import has been queued successfully.');
      await fetchDocuments();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to enqueue onboarding import', err);
      dispatchToastError('Failed to queue onboarding import. Please try again.');
    } finally {
      setIsImporting(false);
    }
  };

  const handleCancelImport = () => {
    setShowConfirmModal(false);
  };

  const onGridReady = (params: any) => {
    gridApiRef.current = params.api;
  };

  const getContextMenuItems = () => ['copy', 'copyWithHeaders', 'separator', 'export'];

  if (!client) return null;
  if (isLoading) return <Loading pageLevel />;

  return (
    <section className="client-onboarding">
      <h3>Onboarding</h3>
      <p>Trigger historical document ingestion from SFTP for this client.</p>
      <div className="client-onboarding__actions">
        <BixButton type="button" onClick={handleBeginImport} disabled={isImporting || isFetchingDocuments}>
          {isImporting ? 'Queuing...' : 'Begin Import'}
        </BixButton>
      </div>
      <Modal title="Begin Import" active={showConfirmModal} onClose={handleCancelImport}>
        <p>
          You are about to ingest documents for <strong>{client.name}</strong>. This will queue a historical document
          import from SFTP. Do you want to proceed?
        </p>
        <div className="modal_button_container">
          <BixButton type="button" onClick={handleConfirmImport}>
            Confirm
          </BixButton>
          <BixButton light type="button" onClick={handleCancelImport}>
            Cancel
          </BixButton>
        </div>
      </Modal>
      {isFetchingDocuments ? (
        <Loading pageLevel />
      ) : (
        <div className="client-onboarding-data-grid">
          <div className="grid_container">
            <div className="grid ag-theme-alpine">
              <AgGridReact
                onGridReady={onGridReady}
                rowData={documents}
                rowHeight={32}
                headerHeight={32}
                defaultColDef={{ flex: 1, minWidth: 100, sortable: true, resizable: true, lockPinned: true }}
                getContextMenuItems={getContextMenuItems}
                suppressDragLeaveHidesColumns={true}
                suppressMovableColumns={true}
                suppressCellFocus={true}
                animateRows={true}
                getRowId={(params: any) => params.data.bixbyId}
                columnDefs={[
                  {
                    headerName: 'Bixby ID',
                    field: 'bixbyId',
                    filter: 'agTextColumnFilter',
                    floatingFilter: true,
                  },
                  {
                    headerName: 'Data Site Name',
                    field: 'dataSiteName',
                    filter: 'agTextColumnFilter',
                    floatingFilter: true,
                  },
                  {
                    headerName: 'Document Name',
                    field: 'documentName',
                    filter: 'agTextColumnFilter',
                    floatingFilter: true,
                  },
                  {
                    headerName: 'Date Posted',
                    field: 'datePosted',
                    filter: 'agDateColumnFilter',
                    floatingFilter: true,
                    valueFormatter: params => (params.value ? new Date(params.value).toLocaleDateString() : ''),
                  },
                  {
                    headerName: 'Status',
                    field: 'status',
                    filter: 'agSetColumnFilter',
                    floatingFilter: true,
                  },
                ]}
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

const mapStateToProps = (state: AppState) => ({
  client: state.clientState.currentClient,
  isLoading: state.clientState.isLoading,
});

const mapDispatchToProps = (dispatch: any) => ({
  dispatchToastSuccess: (message: string) => dispatch(toastSuccess(message)),
  dispatchToastError: (message: string) => dispatch(toastError(message)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ClientOnboarding);
